import { useEffect, useState } from "react";
import ButtonIcon from "components/utility/button/ButtonIcon";
import ButtonIconDownload from "components/utility/button/ButtonIconDownload";
import ContentPageActionsShareModal from "components/page/content-page/ContentPageActionsShareModal";
import { isMobile as checkIfIsMobile } from "components/utility/page-responsive/PageResponsive";
import { searchEndpoints } from "components/utility/api/Api";
import BodyMask from "components/utility/body-mask/BodyMask";
import TooltipButton from "components/utility/tooltip/TooltipButton";
import { v1 as uuidv1 } from "uuid";
import { InteractiveResult } from "components/coveo-headless/components/InteractiveResult";
import { ImageDownload, ImageShare } from "components/utility/images";
import { SearchPageActionsShareProps, SearchPageActionsProps } from "./SearchPageActions";

const SearchPageActionsContent = ({
	data,
	dataset,
	headlessEngine,
	result
}: SearchPageActionsProps) => {
	const [isMobile, setIsMobile] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [share, setShare] = useState<SearchPageActionsShareProps>({});
	const [toggle, setToggle] = useState(false);
	const [closeButton, setCloseButton] = useState(false);

	useEffect(() => {
		const onResize = () => {
			setIsMobile(checkIfIsMobile());
		};
		window.addEventListener("load", onResize);
		window.addEventListener("resize", onResize);
		return () => {
			window.removeEventListener("load", onResize);
			window.removeEventListener("resize", onResize);
		};
	}, []);

	useEffect(() => {
		if (data) {
			setShare(data.share);
		}
	}, [data]);

	const openModal = (type: "mask" | "more" | "favorite" | "share") => {
		if (type === "share") {
			toggleModal();
			shareHandleClick();
		} else {
			setToggle(!toggle);
		}
	};

	const toggleModal = () => {
		if (!share.isDisable) {
			// if share is not disabled, toggle modal
			setIsModalOpen(!isModalOpen);
			if (isMobile) {
				// closes mobile interstitial modal body mask
				setToggle(false);
			}
		}
	};

	const shareHandleClick = () => {
		if (dataset.id && Object.entries(share).length === 0) {
			const apiID = dataset.id;
			let api = `${searchEndpoints.liteShare}${apiID}`;
			const apiVirtualID = dataset.virtualId;
			if (apiVirtualID) {
				api = `${api}&virtualId=${apiVirtualID}`;
			}
			fetch(api, {
				credentials: "same-origin",
				method: "GET",
				headers: {
					"Content-Type": "application/json"
				}
			})
				.then((res) => res.json())
				.then((res) => {
					setShare(() => {
						return res.share;
					});
				})
				.catch((error) => {
					console.log(error);
				});
		} else {
			toggleModal();
		}
	};

	const modalClick = (flag: boolean) => {
		setCloseButton(flag);

		if (isMobile && closeButton) {
			setToggle(false);
			setIsModalOpen(false);
		}
	};

	const getButtons = () => {
		const {
			hideDownload = "false",
			hideFavorite = "false",
			hideShare = "false",
			color = "default",
			downloadUrl = "",
			title = ""
		} = dataset;

		const buttons: JSX.Element[] = [];
		if (hideFavorite.toLowerCase() !== "true") {
			buttons.push(
				<ButtonIcon
					type="favorite"
					key="favorite"
					onClick={() => openModal("favorite")}
					color={color || "default"}
				/>
			);
		}
		if (hideShare.toLowerCase() !== "true") {
			const { isDisable = false, metaData = "" } = share;
			const className = isDisable ? `button-icon-${color}-disable` : "";
			buttons.push(
				<div className={"button-icon-share-wrapper"} key="share">
					{metaData && isDisable ? (
						<TooltipButton
							id={uuidv1()}
							content={metaData}
							position="bottom"
							mobilePosition="bottom"
							tooltipPosition="left"
							mobiletoolPosition="left"
							clickable={true}
						>
							<ButtonIcon
								type="share"
								key="share"
								color={color || "default"}
								className={className}
								isButton={false}
							/>
						</TooltipButton>
					) : headlessEngine && result ? (
						<InteractiveResult
							analytics={{
								"data-a-evt": "Share Initiated",
								"data-a-comp": "Search Card",
								"data-a-name": title
							}}
							ariaLabel="share"
							attribs={{
								class: "button-icon button-icon-default button-share",
								"data-qa": "button-icon-share"
							}}
							headlessEngine={headlessEngine}
							result={result}
							type="button"
							onClick={() => openModal("share")}
						>
							<ImageShare />
						</InteractiveResult>
					) : (
						<ButtonIcon
							type="share"
							key="share"
							onClick={() => openModal("share")}
							color={color || "default"}
							className={className}
							dataAEvt="Share Initiated"
							dataAComp="Search Card"
							dataAName={title}
						/>
					)}
				</div>
			);
		}
		if (hideDownload.toLowerCase() !== "true") {
			if (headlessEngine && result) {
				buttons.push(
					<InteractiveResult
						analytics={{
							"data-a-evt": "Download",
							"data-a-comp": "Search Card",
							"data-a-name": title
						}}
						ariaLabel="download"
						attribs={{
							class: "button-icon button-icon-default button-download",
							download: downloadUrl,
							"data-qa": "button-icon-download"
						}}
						downloadUrl={downloadUrl}
						headlessEngine={headlessEngine}
						result={result}
						type="anchor"
					>
						<ImageDownload />
					</InteractiveResult>
				);
			} else {
				buttons.push(
					<ButtonIconDownload
						downloadUrl={downloadUrl}
						key="download"
						color={color || "default"}
						dataAEvt="Download"
						dataAComp="Search Card"
						dataAName={title}
					/>
				);
			}
		}
		return buttons;
	};

	const getMobileButtons = () => {
		const { color, hideDownload = "false", hideFavorite = "false", hideShare = "false" } = dataset;

		const more: JSX.Element[] = [];
		let hideMore = false;
		if (
			hideDownload.toLowerCase() === "true" &&
			hideFavorite.toLowerCase() === "true" &&
			hideShare.toLowerCase() === "true"
		) {
			hideMore = true;
		}
		if (!hideMore) {
			more.push(
				<ButtonIcon
					type="more"
					key="more"
					onClick={() => openModal("more")}
					color={color || "default"}
				/>
			);
		}
		return more;
	};

	const keyHandler = (e) => {
		const key = e.key;
		if (key === 13 || key === 32) {
			openModal("mask");
		}
	};

	const { title, isGlobalSearch = "false" } = dataset;

	if (isGlobalSearch === "true") {
		return null;
	}

	const className = "content-page";

	return (
		<div className={`${className}-actions`}>
			<BodyMask
				className={toggle ? "body-mask" : ""}
				onClick={() => openModal("mask")}
				onKeyUp={(e) => keyHandler(e)}
			/>
			<div
				className={`${className}-actions-normal` + (toggle ? ` ${className}-actions-mobile` : "")}
			>
				{getButtons()}
			</div>
			<div className={`${className}-actions-more`}>{getMobileButtons()}</div>

			{(data || Object.entries(share).length !== 0) && isModalOpen ? (
				<ContentPageActionsShareModal
					isOpen={isModalOpen}
					setOpenFlag={setIsModalOpen}
					data={share}
					modalClick={modalClick}
					analytics={{
						title,
						comp: "Search Card"
					}}
				/>
			) : null}
		</div>
	);
};

export default SearchPageActionsContent;
